// =============================================================================
// pages/RecurringPage.js — Phase 82.1 (Figma-designed Recurring hub)
//
// Recurring is now a top-level sidebar destination (previously lived under
// /accounts?tab=recurring). This page ports the Figma export's three tabs:
//
//   • Overview     — hero committed-money, category breakdown, upcoming top-5,
//                    Worth Reviewing, and a 6-month trend chart
//   • Upcoming     — 7d / 30d / later filters + payments grouped by date, with
//                    a cash-flow-tightness callout
//   • Commitments  — filter pills + sort + full commitment list with detail
//                    drawer + inline actions
//
// A 4th "Changes" tab from the Figma design was intentionally skipped per user
// direction; the "Worth Reviewing" cards on Overview surface a subset of the
// same signal instead.
//
// Data plumbing
// -------------
// Reads real rows from `recurring_expenses` (schema: name, amount, category,
// frequency, next_due, active, kind, linked_account_id/wealth_id/liability_id,
// created_at). Essentiality is INFERRED (no schema column) from category:
//     housing | insurance | utilities | debt | loan | mortgage → essential
//     everything else → flexible
// CRUD parity (Phase 82.12): Add / Edit / Pause / Resume / Delete / Mark
// paid / Suggestions accept+dismiss are ALL native to this page now.
// The legacy /recurring/manage route redirects here (deprecation shim).
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Plus, ArrowRight, AlertTriangle, ArrowUp, Loader2, X, CheckCircle2,
  Sparkles, Pause, Play, Trash2, Pencil, Repeat,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';
import { formatMoney } from '@/lib/format';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { canonicalKey } from '@/lib/merchantNames';
import { insertTransactionIdempotent } from '@/lib/insertTransaction';
import SectionErrorBoundary from '@/components/SectionErrorBoundary';
import EmptyState from '@/components/EmptyState';
import CommitmentDialog from '@/components/recurring/CommitmentDialog';
import SuggestionsDrawer, { useSuggestionCount } from '@/components/recurring/SuggestionsDrawer';
import { invalidateBriefing } from '@/lib/briefingCache';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// ─── Helpers ────────────────────────────────────────────────────────────────
const FREQ_LABEL = {
  monthly: 'Monthly', annual: 'Annual', yearly: 'Annual', quarterly: 'Quarterly',
  half_yearly: 'Half-yearly',
  weekly: 'Weekly', fortnightly: 'Fortnightly', bimonthly: 'Every 2 months',
};
const FREQ_MULT_MONTHLY = {
  monthly: 1, annual: 1 / 12, yearly: 1 / 12, quarterly: 1 / 3,
  half_yearly: 1 / 6,
  weekly: 52 / 12, fortnightly: 26 / 12, bimonthly: 0.5,
};
const FREQ_MULT_YEARLY = {
  monthly: 12, annual: 1, yearly: 1, quarterly: 4,
  half_yearly: 2,
  weekly: 52, fortnightly: 26, bimonthly: 6,
};

const ESSENTIAL_CATEGORIES = new Set([
  'housing', 'rent', 'mortgage', 'emi',
  'insurance', 'health', 'life insurance', 'vehicle insurance',
  'utilities', 'electricity', 'water', 'gas', 'internet', 'phone',
  'debt', 'loan', 'credit card',
]);

// Category → Figma color and display label. Anything we don't recognise gets
// bucketed as "Other" so the breakdown never breaks on unusual categories.
const CATEGORY_META = {
  housing:       { label: 'Housing',       color: '#163A32' },
  rent:          { label: 'Housing',       color: '#163A32' },
  mortgage:      { label: 'Housing',       color: '#163A32' },
  insurance:     { label: 'Insurance',     color: '#3D7A63' },
  debt:          { label: 'Debt Payments', color: '#A84E38' },
  loan:          { label: 'Debt Payments', color: '#A84E38' },
  subscriptions: { label: 'Subscriptions', color: '#3E6A80' },
  utilities:     { label: 'Utilities',     color: '#A07830' },
};
const OTHER_BUCKET = { label: 'Other', color: '#7A6B4C' };

function categoryBucket(c) {
  const key = (c || '').toLowerCase();
  return CATEGORY_META[key] || OTHER_BUCKET;
}

function inferEssentiality(row) {
  const cat = (row.category || '').toLowerCase();
  if (ESSENTIAL_CATEGORIES.has(cat)) return 'essential';
  if (row.kind === 'debt_payment') return 'essential';
  return 'flexible';
}

// Convert a recurring_expenses row into the shape the UI consumes.
function normaliseRow(row, accountsById = {}) {
  const freq = (row.frequency || 'monthly').toLowerCase();
  const linked =
    accountsById[row.linked_account_id] ||
    accountsById[row.linked_wealth_id] ||
    accountsById[row.linked_liability_id] ||
    null;
  // Phase 84.22.5 — infer the commitment's native currency from the
  // linked account/holding/liability. `recurring_expenses` has no
  // `currency` column of its own, so this join at read-time is the
  // source of truth for how the amount should render.
  const currency = (linked?.currency || row.currency || 'INR').toUpperCase();
  return {
    id: row.id,
    name: row.name,
    merchant: row.name, // Schema has no separate merchant column — reuse name.
    amount: Number(row.amount || 0),
    currency,
    frequency: freq,
    nextDate: row.next_due || null,
    category: (row.category || 'other').toLowerCase(),
    kind: row.kind || 'expense',
    essentiality: inferEssentiality(row),
    active: row.active !== false,
    account: linked?.name || '—',
    accountId: row.linked_account_id || row.linked_wealth_id || row.linked_liability_id,
    createdAt: row.created_at,
    lastReviewed: row.last_reviewed || null,
    // Derived
    monthlyEq: (Number(row.amount) || 0) * (FREQ_MULT_MONTHLY[freq] || 1),
    annualCost: (Number(row.amount) || 0) * (FREQ_MULT_YEARLY[freq] || 12),
  };
}

function daysBetween(a, b) {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

// Project upcoming payments for the next 90 days based on next_due + frequency.
// If a next_due is in the past, roll it forward until it's in the future.
function projectUpcoming(commitments, horizonDays = 90) {
  const out = [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const limit  = new Date(today.getTime() + horizonDays * 86400000);

  commitments.forEach((c) => {
    if (!c.nextDate || !c.active) return;
    let dt = new Date(c.nextDate);
    // Roll forward past due dates so we display "next" not "was".
    // Phase 84.23.7 — use CADENCE_DAYS as the single source of truth so
    // 'half_yearly' and any future cadence added upstream projects
    // correctly (previously an inline table drifted and half-yearly
    // would over-project by ~6x).
    const stepDays = CADENCE_DAYS[c.frequency] || 30;
    let guard = 0;
    while (dt < today && guard < 24) { dt = new Date(dt.getTime() + stepDays * 86400000); guard += 1; }
    while (dt <= limit) {
      out.push({
        id: `${c.id}-${dt.toISOString().slice(0, 10)}`,
        commitmentId: c.id,
        merchant: c.merchant,
        amount: c.amount,
        // Phase 84.22.5 — the commitment's native currency and its base-
        // currency equivalent, so upcoming-payment aggregates work for
        // multi-currency portfolios and individual rows can still render
        // in native.
        currency: c.currency,
        amountBase: c.amountBase != null ? c.amountBase : c.amount,
        date: dt.toISOString().slice(0, 10),
        account: c.account,
        frequency: c.frequency,
        // Real "paymentStatus" needs transaction matching to detect
        // amount-changed / date-changed / paid — we default to 'expected'
        // and rely on the "Mark paid" action on the commitment drawer for
        // manual reconciliation.
        paymentStatus: 'expected',
      });
      // Only project one occurrence per commitment for annual+ cadences to
      // keep the list compact; project multiple months for monthly cadences.
      if (c.frequency === 'annual' || c.frequency === 'yearly') break;
      dt = new Date(dt.getTime() + stepDays * 86400000);
    }
  });
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

const fmtDateFull = (s) => new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtMonthAbbr = (s) => new Date(s).toLocaleDateString('en-IN', { month: 'short' });

// ─── Data hook ───────────────────────────────────────────────────────────────
// Frequency → number of days to advance `next_due` when a payment is marked
// paid. Matches the legacy Recurring.js FREQ.days table exactly so the same
// cadence math applies whether the user marks paid from the new hub or the
// (soon-to-be-retired) legacy page.
const FREQ_DAYS = {
  monthly: 30, fortnightly: 14, weekly: 7,
  quarterly: 92, yearly: 365, annual: 365, bimonthly: 61,
};

// Map a recurring commitment `kind` to the transaction `type` we insert when
// the user marks a cycle paid/received. Preserves the legacy semantics so
// downstream Insights/Reflections categorisation is consistent.
const KIND_TO_TXN_TYPE = {
  income: 'income',
  expense: 'expense',
  investment: 'investment',
  debt_payment: 'expense', // debt payments are expense-shaped for now
  transfer: 'transfer',
};

function useRecurringData() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [accountsById, setAccountsById] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumping this triggers a full reload without unmounting the page. Every
  // mutation helper below flips it after a successful call.
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        // 12-month transaction window — long enough for the "commitment
        // stopped" detector to see two full cycles of quarterly cadences
        // (we skip annual/yearly for stopped-detection because a 12-month
        // window can't reliably distinguish "annual not paid yet this year"
        // from "annual stopped").
        const start = new Date();
        start.setMonth(start.getMonth() - 12);
        start.setDate(1);
        const startISO = start.toISOString().slice(0, 10);

        // Load transactions. The `recurring_id` column is optional (added in
        // Phase 82.6 migration). If it hasn't landed yet, the query fails —
        // we retry without it so the page keeps working. Matching then falls
        // back to canonicalKey() only.
        const buildTxnQuery = (withLink) => supabase
          .from('transactions')
          .select(withLink
            ? 'id, txn_date, amount, category, description, type, recurring_id'
            : 'id, txn_date, amount, category, description, type')
          .gte('txn_date', startISO)
          .eq('type', 'expense')
          .limit(5000);

        const [recR, accR, whR, liaR] = await Promise.all([
          supabase.from('recurring_expenses').select('*').order('next_due', { ascending: true }),
          supabase.from('accounts').select('id, name, currency'),
          supabase.from('wealth_holdings').select('id, name, currency'),
          supabase.from('liabilities').select('id, name, currency'),
        ]);
        let txnR = await buildTxnQuery(true);
        if (txnR.error && String(txnR.error.message || '').toLowerCase().includes('recurring_id')) {
          // Phase 82.6 migration not yet run — silently retry without it.
          txnR = await buildTxnQuery(false);
        }
        if (cancelled) return;
        const byId = {};
        (accR.data || []).forEach((a) => { byId[a.id] = a; });
        (whR.data  || []).forEach((a) => { byId[a.id] = a; });
        (liaR.data || []).forEach((a) => { byId[a.id] = a; });
        setAccountsById(byId);
        setRows(recR.data || []);
        setTransactions(txnR.data || []);
      } catch (e) { console.warn('[recurring] load failed:', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.id, reloadKey]);

  const commitments = useMemo(
    () => rows.map((r) => normaliseRow(r, accountsById)),
    [rows, accountsById],
  );

  // Optimistic "mark reviewed" mutation. Writes last_reviewed = now(), then
  // patches local state so the drawer + Worth Reviewing card reflect the
  // change immediately. Falls back to a toast + revert on failure.
  const markReviewed = async (commitmentId) => {
    const nowIso = new Date().toISOString();
    const prev = rows;
    setRows((cur) => cur.map((r) => (r.id === commitmentId ? { ...r, last_reviewed: nowIso } : r)));
    const { error } = await supabase
      .from('recurring_expenses')
      .update({ last_reviewed: nowIso })
      .eq('id', commitmentId);
    if (error) {
      setRows(prev);
      // Schema migration may not have landed yet — surface a clear message.
      if ((error.message || '').toLowerCase().includes('last_reviewed')) {
        toast.error(
          "We're finishing a quick database update. 'Mark reviewed' will work once it's ready.",
        );
      } else {
        toast.error(error.message || 'Could not mark as reviewed.');
      }
      return false;
    }
    // Phase 84.5 — the briefing surfaces "Worth Reviewing" NBAs based on
    // last_reviewed timestamps; invalidate so the Overview page recomposes.
    invalidateBriefing();
    toast.success('Marked as reviewed.');
    return true;
  };

  // Pause / resume — toggle the `active` flag. Optimistic update so the
  // drawer's action state feels instantaneous.
  const togglePause = async (commitment) => {
    const nextActive = !commitment.active;
    const prev = rows;
    setRows((cur) => cur.map((r) => (r.id === commitment.id ? { ...r, active: nextActive } : r)));
    const { error } = await supabase
      .from('recurring_expenses')
      .update({ active: nextActive })
      .eq('id', commitment.id);
    if (error) {
      setRows(prev);
      toast.error(error.message || 'Could not update.');
      return false;
    }
    invalidateBriefing();
    toast.success(nextActive ? 'Commitment resumed.' : 'Commitment paused.');
    return true;
  };

  // Delete — hard delete of the recurring rule. We rely on the parent's
  // AlertDialog to confirm intent before this fires.
  const deleteCommitment = async (commitment) => {
    const prev = rows;
    setRows((cur) => cur.filter((r) => r.id !== commitment.id));
    const { error } = await supabase
      .from('recurring_expenses')
      .delete()
      .eq('id', commitment.id);
    if (error) {
      setRows(prev);
      toast.error(error.message || 'Could not delete.');
      return false;
    }
    invalidateBriefing();
    toast.success('Commitment removed.');
    return true;
  };

  // Mark this cycle paid/received: insert the transaction idempotently
  // (parity with legacy Recurring.js) and advance `next_due` by one cadence.
  const markPaid = async (commitment) => {
    if (!user?.id) return false;
    const step = FREQ_DAYS[(commitment.frequency || '').toLowerCase()] || 30;
    const baseDate = commitment.nextDate || new Date().toISOString().slice(0, 10);
    const nextDueDate = new Date(baseDate);
    nextDueDate.setDate(nextDueDate.getDate() + step);
    const newDueISO = nextDueDate.toISOString().slice(0, 10);
    const txnType = KIND_TO_TXN_TYPE[commitment.kind] || 'expense';
    try {
      await insertTransactionIdempotent({
        user_id: user.id,
        amount: Number(commitment.amount),
        type: txnType,
        category: commitment.category,
        description: `${commitment.name} (recurring)`,
        txn_date: baseDate,
        recurring: true,
        source: 'manual',
      });
      const { error } = await supabase
        .from('recurring_expenses')
        .update({ next_due: newDueISO })
        .eq('id', commitment.id);
      if (error) throw error;
      // Optimistic patch — parent drawer relies on `nextDate` to render.
      setRows((cur) => cur.map((r) => (r.id === commitment.id ? { ...r, next_due: newDueISO } : r)));
      // Phase 84.5 — a new txn + a rolled-forward next_due both feed
      // briefing signals; invalidate so Overview recomposes on next view.
      invalidateBriefing();
      const isIncomeLike = commitment.kind === 'income';
      toast.success(isIncomeLike
        ? 'Marked received. Next expected date rolled forward.'
        : 'Marked paid. Next due date rolled forward.');
      return newDueISO;
    } catch (e) {
      toast.error(e?.message || 'Could not mark paid.');
      return false;
    }
  };

  return {
    commitments, transactions, loading,
    refresh, markReviewed, togglePause, deleteCommitment, markPaid,
  };
}

// ─── Shared primitives ──────────────────────────────────────────────────────
function Card({ children, className = '', style, testId }) {
  return (
    <div
      className={`bg-card rounded-xl border border-border ${className}`}
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...style }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function Pill({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
        active
          ? 'bg-evergreen text-white border-evergreen'
          : 'bg-transparent text-muted-foreground border-border hover:border-evergreen/40'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Overview tab ───────────────────────────────────────────────────────────
function OverviewTab({ commitments, transactions, baseCurrency, onGoToTab, onOpenCommitment }) {
  // Real totals from the data (no fabricated numbers). Phase 84.22.5:
  // every rollup uses the FX-converted `monthlyEqBase` / `amountBase`
  // fields so multi-currency portfolios add up in a single headline
  // number (base currency).
  const active = commitments.filter((c) => c.active);
  const monthlyTotal = active.reduce((s, c) => s + (c.monthlyEqBase ?? c.monthlyEq), 0);
  const next30 = projectUpcoming(active, 30);
  const next30Total = next30.reduce((s, u) => s + (u.amountBase ?? u.amount), 0);

  // "Needs review" heuristic: prefer `last_reviewed` timestamp (Phase 82.3
  // schema); fall back to `created_at` when the column is NULL. Anything
  // older than 180 days is flagged. NULL last_reviewed on an old commitment
  // will show up first because coalesce yields created_at.
  const staleThreshold = Date.now() - (180 * 86400000);
  const stalenessOf = (c) => {
    const base = c.lastReviewed || c.createdAt;
    return base ? new Date(base).getTime() : 0;
  };
  const needsReview = active
    .filter((c) => stalenessOf(c) > 0 && stalenessOf(c) < staleThreshold)
    .sort((a, b) => stalenessOf(a) - stalenessOf(b));

  // Category breakdown (Figma "Where Your Committed Money Goes")
  const catMap = new Map();
  active.forEach((c) => {
    const bucket = categoryBucket(c.category);
    const prev = catMap.get(bucket.label) || { label: bucket.label, color: bucket.color, amount: 0 };
    prev.amount += (c.monthlyEqBase ?? c.monthlyEq);
    catMap.set(bucket.label, prev);
  });
  const categoryBreakdown = Array.from(catMap.values()).sort((a, b) => b.amount - a.amount);
  const maxCat = Math.max(1, ...categoryBreakdown.map((c) => c.amount));

  // 6-month trend: sum of transactions matching recurring commitment names by
  // month. This gives a defensible "committed spending" trend from real data.
  // If we don't have enough tx history, we fall back to a flat line at the
  // current monthly total.
  const trendData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ key, month: d.toLocaleDateString('en-IN', { month: 'short' }), committed: 0 });
    }
    const nameSet = new Set(active.map((c) => (c.name || '').toLowerCase()));
    (transactions || []).forEach((t) => {
      const desc = (t.category || '').toLowerCase(); // best-effort: match by category
      const k = String(t.txn_date || '').slice(0, 7);
      const row = months.find((m) => m.key === k);
      if (row && (nameSet.has(desc) || nameSet.size === 0)) {
        row.committed += Math.abs(Number(t.amount) || 0);
      }
    });
    // Phase 85.6 — this used to back-fill every month with the current total
    // when no transaction matched, so a person with no history was shown a flat
    // six-month line that was really one number repeated. A chart of zeros is
    // honest; an invented series is not.
    return months;
  }, [transactions, active, monthlyTotal]);

  // A trend needs real periods to be a trend. Three months of actual recorded
  // committed spending is the floor; below that we say so rather than calling a
  // zero delta "steady", because no data and no change look identical.
  const realTrendMonths = trendData.filter((m) => m.committed > 0).length;
  const sufficientTrendHistory = realTrendMonths >= 3;
  const trendFirst = trendData[0]?.committed || monthlyTotal;
  const trendLast  = trendData[trendData.length - 1]?.committed || monthlyTotal;
  const trendDelta = trendLast - trendFirst;
  const trendPct   = trendFirst > 0 ? Math.round(((trendLast - trendFirst) / trendFirst) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="recurring-overview">
      {/* Hero */}
      <Card className="p-6 lg:p-8">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-3">
          Your Committed Money
        </p>
        <div className="flex items-end gap-4 flex-wrap">
          <p className="font-display text-4xl lg:text-5xl font-medium text-charcoal tabular-nums leading-none"
            style={{ letterSpacing: '-0.025em' }}
            data-testid="recurring-monthly-total"
          >
            {formatMoney(monthlyTotal, baseCurrency, { decimals: 0 })}
          </p>
          <p className="text-muted-foreground text-base mb-1">/ month</p>
        </div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {active.length} active commitment{active.length === 1 ? '' : 's'}, committed before everyday spending begins.
        </p>
        {sufficientTrendHistory && trendDelta !== 0 && (
          <div className="flex items-center gap-1.5 mt-3">
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
              style={{
                backgroundColor: trendDelta > 0 ? '#FBF5E8' : '#EEF2EF',
                color:           trendDelta > 0 ? '#B07210' : '#3D7A63',
              }}
              data-testid="recurring-trend-delta"
            >
              <ArrowUp className={`h-3 w-3 ${trendDelta < 0 ? 'rotate-180' : ''}`} />
              {formatMoney(Math.abs(trendDelta), baseCurrency, { decimals: 0 })} compared with six months ago
            </span>
          </div>
        )}

        {/* 4 rollup metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border/60">
          {[
            { label: 'Monthly Committed', value: formatMoney(monthlyTotal, baseCurrency, { decimals: 0 }), color: '#163A32' },
            { label: 'Next 30 Days',      value: formatMoney(next30Total, baseCurrency, { decimals: 0 }),  color: '#B07210' },
            { label: 'Annual Cost',       value: formatMoney(monthlyTotal * 12, baseCurrency, { decimals: 0, compact: true }), color: '#1E1E1E' },
            { label: 'Needs Review',      value: `${needsReview.length} commitment${needsReview.length === 1 ? '' : 's'}`, color: '#A84E38' },
          ].map((m) => (
            <div key={m.label}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium mb-1.5">{m.label}</p>
              <p className="tabular-nums font-display text-lg font-medium" style={{ color: m.color }}>{m.value}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Recurring Briefing (real numbers, generic voice) */}
      <Card className="p-5 lg:p-6" style={{ borderLeft: '3px solid #163A32' }}>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold mb-3">
          Your Recurring Briefing
        </p>
        {sufficientTrendHistory && trendDelta !== 0 ? (
          <>
            <p className="text-sm text-charcoal/85 leading-relaxed mb-2">
              Your committed spending has {trendDelta > 0 ? 'increased' : 'decreased'} by roughly{' '}
              <strong className="text-charcoal font-medium">{formatMoney(Math.abs(trendDelta), baseCurrency, { decimals: 0 })}</strong>{' '}
              over the last six months ({trendPct > 0 ? '+' : ''}{trendPct}%).
            </p>
            <p className="text-sm text-charcoal/70 leading-relaxed mb-4">
              If this pattern continues, your recurring commitments will {trendDelta > 0 ? 'cost' : 'save'} approximately{' '}
              <strong className="text-charcoal font-medium">{formatMoney(Math.abs(trendDelta) * 12, baseCurrency, { decimals: 0 })}</strong> over the next year.
            </p>
          </>
        ) : sufficientTrendHistory ? (
          <p className="text-sm text-charcoal/85 leading-relaxed mb-4" data-testid="recurring-trend-steady">
            Your committed spending has been steady over the last six months.
          </p>
        ) : (
          <p className="text-sm text-charcoal/85 leading-relaxed mb-4" data-testid="recurring-trend-insufficient">
            Not enough history yet to show a six-month trend.
          </p>
        )}
        <button
          type="button"
          onClick={() => onGoToTab('changes')}
          className="flex items-center gap-1.5 text-xs font-medium text-evergreen transition-colors hover:opacity-80"
          data-testid="recurring-briefing-review-changes"
        >
          Review what changed <ArrowRight className="h-3 w-3" />
        </button>
      </Card>

      {/* Category breakdown */}
      {categoryBreakdown.length > 0 && (
        <Card className="p-5 lg:p-6" testId="recurring-category-breakdown">
          <p className="text-xs font-semibold text-charcoal mb-5">Where Your Committed Money Goes</p>
          <div className="space-y-3">
            {categoryBreakdown.map((cat) => (
              <div key={cat.label} className="flex items-center gap-4">
                <p className="text-sm text-charcoal/85 w-28 flex-shrink-0">{cat.label}</p>
                <div className="flex-1 h-2 bg-border/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${(cat.amount / maxCat) * 100}%`, backgroundColor: cat.color }}
                  />
                </div>
                <p className="tabular-nums text-sm font-semibold text-charcoal w-24 text-right flex-shrink-0">
                  {formatMoney(cat.amount, baseCurrency, { decimals: 0 })}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Upcoming (top 5) */}
      {next30.length > 0 && (
        <Card className="overflow-hidden" testId="recurring-upcoming-preview">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/60 bg-offwhite/40">
            <p className="text-xs font-semibold text-charcoal">Upcoming Commitments</p>
            <button
              type="button"
              onClick={() => onGoToTab('upcoming')}
              className="text-xs text-muted-foreground hover:text-evergreen font-medium transition-colors flex items-center gap-1"
              data-testid="recurring-upcoming-view-all"
            >
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-border/40">
            {next30.slice(0, 5).map((u) => (
              <div key={u.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-10 text-center flex-shrink-0">
                  <p className="text-[10px] text-muted-foreground/60 uppercase font-medium">{fmtMonthAbbr(u.date)}</p>
                  <p className="tabular-nums text-base font-semibold text-charcoal leading-tight">{new Date(u.date).getDate()}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal truncate">{u.merchant}</p>
                  <p className="text-[11px] text-muted-foreground/70">{u.account} · {FREQ_LABEL[u.frequency] || u.frequency}</p>
                </div>
                <p className="tabular-nums text-sm font-semibold text-charcoal flex-shrink-0">
                  {formatMoney(u.amount, u.currency || baseCurrency, { decimals: 0 })}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Worth Reviewing */}
      {needsReview.length > 0 && (
        <Card className="overflow-hidden" testId="recurring-worth-reviewing">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border/60 bg-offwhite/40">
            <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#B07210' }} />
            <p className="text-xs font-semibold text-charcoal">Worth Reviewing</p>
          </div>
          <div className="px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#B07210' }} />
              <div className="flex-1">
                <p className="text-sm font-medium text-charcoal">
                  {needsReview.length} commitment{needsReview.length === 1 ? '' : 's'} haven&apos;t been reviewed in 6+ months
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                  Together they represent{' '}
                  {formatMoney(needsReview.reduce((s, c) => s + (c.monthlyEqBase ?? c.monthlyEq), 0), baseCurrency, { decimals: 0 })}/month.
                  A periodic check can catch price rises and duplicate services.
                </p>
                <button
                  type="button"
                  onClick={() => onOpenCommitment(needsReview[0])}
                  className="text-xs font-medium flex items-center gap-1 transition-colors hover:opacity-70"
                  style={{ color: '#B07210' }}
                >
                  Start with {needsReview[0].name} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Trend chart */}
      <Card className="p-5 lg:p-6" testId="recurring-trend-chart">
        <p className="text-xs font-semibold text-charcoal mb-1">Committed Money Trend</p>
        <p className="text-xs text-muted-foreground mb-4">Last 6 months of matching recurring spend</p>

        <div style={{ height: 180, width: '100%', minHeight: 180 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={trendData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="committedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#163A32" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#163A32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.6} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '11px',
                }}
                formatter={(v) => formatMoney(Number(v), baseCurrency, { decimals: 0 })}
              />
              <Area type="monotone" dataKey="committed" stroke="#163A32" strokeWidth={1.8} fill="url(#committedGrad)" dot={false}
                activeDot={{ r: 4, fill: '#163A32', strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

// ─── Upcoming tab ───────────────────────────────────────────────────────────
function UpcomingTab({ commitments, baseCurrency }) {
  const [filter, setFilter] = useState('30d');

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString().slice(0, 10);
  const all = useMemo(() => projectUpcoming(commitments.filter((c) => c.active), 365), [commitments]);

  const filtered = useMemo(() => all.filter((u) => {
    const days = daysBetween(u.date, todayISO);
    if (filter === '7d')  return days >= 0 && days <= 7;
    if (filter === '30d') return days >= 0 && days <= 30;
    return days > 30;
  }), [all, filter, todayISO]);

  const total = filtered.reduce((s, u) => s + (u.amountBase ?? u.amount), 0);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((u) => {
      if (!map.has(u.date)) map.set(u.date, []);
      map.get(u.date).push(u);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // Detect a tight 7-day cash-flow window in the next 30 days.
  const cashFlowWindow = useMemo(() => {
    if (filter === 'later') return null;
    const next30 = all.filter((u) => {
      const d = daysBetween(u.date, todayISO);
      return d >= 0 && d <= 30;
    });
    if (next30.length < 3) return null;
    let best = { start: null, end: null, total: 0 };
    for (let i = 0; i < next30.length; i += 1) {
      const start = next30[i].date;
      const endLimit = new Date(new Date(start).getTime() + 7 * 86400000).toISOString().slice(0, 10);
      const inWin = next30.filter((u) => u.date >= start && u.date <= endLimit);
      const t = inWin.reduce((s, u) => s + (u.amountBase ?? u.amount), 0);
      if (t > best.total) best = { start, end: inWin[inWin.length - 1].date, total: t };
    }
    return best.total > total * 0.4 && best.start !== best.end ? best : null;
  }, [all, filter, todayISO, total]);

  const filterLabels = { '7d': 'Next 7 Days', '30d': 'Next 30 Days', 'later': 'Later' };

  return (
    <div className="space-y-5" data-testid="recurring-upcoming">
      {/* Hero */}
      <Card className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-2">
              Expected — {filterLabels[filter]}
            </p>
            <p className="font-display text-3xl font-medium text-charcoal tabular-nums"
              style={{ letterSpacing: '-0.02em' }}
              data-testid="recurring-upcoming-total"
            >
              {formatMoney(total, baseCurrency, { decimals: 0 })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {filtered.length} payment{filtered.length === 1 ? '' : 's'} expected
            </p>
          </div>
          <div className="flex gap-1 flex-shrink-0" role="tablist">
            {[
              { id: '7d', label: '7 days' },
              { id: '30d', label: '30 days' },
              { id: 'later', label: 'Later' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`recurring-upcoming-filter-${f.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filter === f.id ? 'bg-evergreen text-white' : 'bg-offwhite text-muted-foreground hover:text-charcoal'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Cash-flow tightness callout */}
      {cashFlowWindow && (
        <div
          className="flex items-start gap-3 px-4 py-3.5 rounded-xl border"
          style={{ borderColor: '#D9A44150', backgroundColor: '#FBF5E8' }}
          data-testid="recurring-cash-flow-note"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#B07210' }} />
          <div>
            <p className="text-sm font-medium text-charcoal">
              Cash flow note — {new Date(cashFlowWindow.start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              &nbsp;to {new Date(cashFlowWindow.end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              <strong className="text-charcoal font-medium">
                {formatMoney(cashFlowWindow.total, baseCurrency, { decimals: 0 })}
              </strong>{' '}
              of known commitments are expected in a 7-day window. Ensure your funding account has sufficient balance.
            </p>
          </div>
        </div>
      )}

      {/* Grouped list */}
      <div className="space-y-4">
        {grouped.map(([date, payments]) => (
          <Card key={date} className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border/60 bg-offwhite/40">
              <p className="text-xs font-semibold text-charcoal">{fmtDateFull(date)}</p>
              <p className="tabular-nums text-xs font-medium text-muted-foreground">
                {formatMoney(payments.reduce((s, p) => s + (p.amountBase ?? p.amount), 0), baseCurrency, { decimals: 0 })}
              </p>
            </div>
            <div className="divide-y divide-border/40">
              {payments.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-offwhite/40 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal">{u.merchant}</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                      {u.account} · {FREQ_LABEL[u.frequency] || u.frequency}
                    </p>
                  </div>
                  <p className="tabular-nums text-sm font-semibold text-charcoal flex-shrink-0">
                    {formatMoney(u.amount, u.currency || baseCurrency, { decimals: 0 })}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {grouped.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">No payments expected in this window.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Commitments tab ────────────────────────────────────────────────────────
const COMMITMENT_FILTERS = [
  { id: 'all',           label: 'All' },
  { id: 'essential',     label: 'Essential' },
  { id: 'flexible',      label: 'Flexible' },
  { id: 'debt',          label: 'Debt Payments' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'annual',        label: 'Annual / Irregular' },
];

function CommitmentsTab({ commitments, baseCurrency, onOpenCommitment }) {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('next');

  const shown = useMemo(() => {
    let list = commitments.filter((c) => c.active);
    if (filter === 'essential')          list = list.filter((c) => c.essentiality === 'essential');
    else if (filter === 'flexible')      list = list.filter((c) => c.essentiality === 'flexible');
    else if (filter === 'debt')          list = list.filter((c) => c.kind === 'debt_payment' || c.category === 'debt' || c.category === 'loan');
    else if (filter === 'subscriptions') list = list.filter((c) => c.category === 'subscriptions');
    else if (filter === 'annual')        list = list.filter((c) => c.frequency !== 'monthly');

    if (sortBy === 'amount') return [...list].sort((a, b) => (b.monthlyEqBase ?? b.monthlyEq) - (a.monthlyEqBase ?? a.monthlyEq));
    if (sortBy === 'name')   return [...list].sort((a, b) => a.name.localeCompare(b.name));
    return [...list].sort((a, b) => (a.nextDate || '9999').localeCompare(b.nextDate || '9999'));
  }, [commitments, filter, sortBy]);

  const flexibleTotal = commitments.filter((c) => c.active && c.essentiality === 'flexible').reduce((s, c) => s + (c.monthlyEqBase ?? c.monthlyEq), 0);

  return (
    <div className="space-y-5" data-testid="recurring-commitments">
      {/* Flexibility callout */}
      {flexibleTotal > 0 && (
        <Card className="flex items-start gap-3 px-5 py-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-charcoal">
              <span className="font-semibold tabular-nums">{formatMoney(flexibleTotal, baseCurrency, { decimals: 0 })}/month</span>
              {' '}of your committed spending is potentially flexible.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Subscriptions, memberships and discretionary recurring spending that you could revisit.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFilter('flexible')}
            className="text-xs font-medium flex-shrink-0 transition-colors hover:opacity-70 flex items-center gap-1"
            style={{ color: '#3E6A80' }}
            data-testid="recurring-view-flexible"
          >
            View flexible <ArrowRight className="h-3 w-3" />
          </button>
        </Card>
      )}

      {/* Filter pills + sort */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {COMMITMENT_FILTERS.map((f) => (
          <Pill
            key={f.id}
            active={filter === f.id}
            onClick={() => setFilter(f.id)}
            testId={`recurring-filter-${f.id}`}
          >
            {f.label}
          </Pill>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground/70 uppercase tracking-widest font-medium">Sort</span>
          {[
            { id: 'next',   label: 'Due date' },
            { id: 'amount', label: 'Amount' },
            { id: 'name',   label: 'Name' },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSortBy(s.id)}
              data-testid={`recurring-sort-${s.id}`}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
                sortBy === s.id ? 'bg-evergreen/10 text-evergreen' : 'text-muted-foreground hover:text-charcoal'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <Card className="overflow-hidden">
        <div className="hidden lg:grid grid-cols-12 gap-4 px-5 py-3 border-b border-border/60 bg-offwhite/40 text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium">
          <div className="col-span-4">Name</div>
          <div className="col-span-1">Category</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-2 text-right">Annual cost</div>
          <div className="col-span-2">Next payment</div>
          <div className="col-span-2">Account</div>
        </div>
        <div className="divide-y divide-border/40">
          {shown.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onOpenCommitment(c)}
              data-testid={`recurring-row-${c.id}`}
              className="w-full text-left px-5 py-4 hover:bg-offwhite/40 transition-colors group"
            >
              <div className="flex items-start gap-3 lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center">
                <div className="flex-1 lg:col-span-4 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-charcoal">{c.name}</p>
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize"
                      style={{
                        backgroundColor: c.essentiality === 'essential' ? '#EEF2EF' : '#EEF5FA',
                        color:           c.essentiality === 'essential' ? '#163A32' : '#3E6A80',
                      }}
                    >
                      {c.essentiality}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground/70 mt-0.5 lg:hidden">
                    {FREQ_LABEL[c.frequency] || c.frequency} · {formatMoney(c.amount, c.currency || baseCurrency, { decimals: 0 })} · {c.account}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground hidden lg:block lg:col-span-1 capitalize truncate">{c.category}</p>
                <p className="tabular-nums text-sm font-semibold text-charcoal hidden lg:block lg:col-span-1 text-right">
                  {formatMoney(c.amount, c.currency || baseCurrency, { decimals: 0 })}
                </p>
                <p className="tabular-nums text-sm text-muted-foreground/80 hidden lg:block lg:col-span-2 text-right">
                  {formatMoney(c.annualCost, c.currency || baseCurrency, { decimals: 0 })}
                </p>
                <p className="text-sm text-charcoal/80 hidden lg:block lg:col-span-2">
                  {c.nextDate ? fmtDateFull(c.nextDate) : '—'}
                </p>
                <p className="text-xs text-muted-foreground/80 truncate hidden lg:block lg:col-span-2">{c.account}</p>
              </div>
            </button>
          ))}
          {shown.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm text-muted-foreground">No commitments match this filter.</p>
            </div>
          )}
        </div>
        {shown.length > 0 && (
          <div className="px-5 py-3 border-t border-border/60 bg-offwhite/40 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {shown.length} commitment{shown.length === 1 ? '' : 's'}
            </p>
            <p className="tabular-nums text-xs font-semibold text-charcoal">
              {formatMoney(shown.reduce((s, c) => s + (c.monthlyEqBase ?? c.monthlyEq), 0), baseCurrency, { decimals: 0 })}/month equivalent
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Change detection ──────────────────────────────────────────────────────
// Pure function — given commitments + last-12-months of transactions, returns
// a list of change signals grouped by type. Each signal has stable id,
// headline, detail, impact, and optional commitmentId so the UI can link.
//
// Matching approach (Phase 82.4 refinement)
// -----------------------------------------
// Previously we did a fragile substring match against transaction.category.
// This is now replaced with `canonicalKey()` from lib/merchantNames.js —
// the same resolver the backend recurring-suggestions detector uses, so
// server-detected recurring rules and client-detected changes stay in
// lockstep.
//
// For each transaction we compute:
//   canonicalKey(t.description || t.category)
// and index by that key. Every commitment then looks up its own
// canonicalKey(commitment.name) → gets a real, deterministic transaction
// list. When a proper transaction↔commitment FK lands later, this
// function can be swapped to consume that link directly without touching
// the detector rules below.
//
// Detection rules
// ---------------
//   • new-commitment      → created_at < 60 days ago
//   • annual-approaching  → frequency ∈ {annual, yearly} & next_due < 30 days
//   • price-change        → ≥ 3 matched txns, average differs from tracked
//                           amount by ≥ 10%, and the most recent tx alone
//                           agrees with the average sign (guards against a
//                           single spike moving the mean). This is stricter
//                           than the previous rule and cuts false positives.
//   • commitment-stopped  → no matched txn in ≥ 2 cadence cycles for cadences
//                           ≤ quarterly (skipped for annual/yearly — 12mo
//                           window is too short to distinguish "not this
//                           year yet" from "stopped").
//   • duplicate           → ≥ 2 active subscription-category commitments.

// Expected days between charges for a cadence. Used by the "commitment
// stopped" detector to decide "how long since a payment is too long".
const CADENCE_DAYS = {
  weekly: 7, fortnightly: 14, bimonthly: 61,
  monthly: 30, quarterly: 91, half_yearly: 182, annual: 365, yearly: 365,
};

// Build per-commitment matched-txn lists, preferring the authoritative FK
// `transactions.recurring_id` (Phase 82.6 migration) when populated and
// falling back to canonicalKey() otherwise. Reconciled links are strictly
// stronger than fuzzy matching; when a link exists we don't merge in any
// canonical-key matches so a stale key match can't pollute a reconciled set.
function buildCommitmentMatches(commitments, transactions) {
  // Bucket by FK first
  const byRecurringId = new Map();
  const unlinked = [];
  transactions.forEach((t) => {
    if (t.recurring_id) {
      if (!byRecurringId.has(t.recurring_id)) byRecurringId.set(t.recurring_id, []);
      byRecurringId.get(t.recurring_id).push(t);
    } else {
      unlinked.push(t);
    }
  });
  // Bucket the unlinked rest by canonical key so fallback matching stays O(1)
  const byCanonicalKey = new Map();
  unlinked.forEach((t) => {
    const key = canonicalKey(t.description || t.category || '');
    if (!key) return;
    if (!byCanonicalKey.has(key)) byCanonicalKey.set(key, []);
    byCanonicalKey.get(key).push(t);
  });

  const perCommitment = new Map();
  commitments.forEach((c) => {
    const linked = byRecurringId.get(c.id) || [];
    let matches = linked;
    let source = 'reconciled';
    if (linked.length === 0) {
      const cKey = canonicalKey(c.name || '');
      matches = cKey ? (byCanonicalKey.get(cKey) || []) : [];
      source = 'canonical';
    }
    // Always chronologically ascending so callers can trust the order.
    matches = [...matches].sort((a, b) => (a.txn_date || '').localeCompare(b.txn_date || ''));
    perCommitment.set(c.id, { matches, source });
  });
  return perCommitment;
}

function detectChanges({ commitments, transactions }) {
  const out = [];
  const active = commitments.filter((c) => c.active);
  const now = Date.now();
  const matchIndex = buildCommitmentMatches(active, transactions);

  active.forEach((c) => {
    const { matches, source } = matchIndex.get(c.id) || { matches: [], source: 'canonical' };

    // ── new-commitment ────────────────────────────────────────────────
    if (c.createdAt) {
      const ageDays = (now - new Date(c.createdAt).getTime()) / 86400000;
      if (ageDays >= 0 && ageDays < 60) {
        out.push({
          id: `new-${c.id}`,
          type: 'new-commitment',
          commitmentId: c.id,
          merchant: c.name,
          date: c.createdAt.slice(0, 10),
          headline: `New commitment detected — ${c.name}`,
          detail: `${FREQ_LABEL[c.frequency] || c.frequency} · First tracked ${new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.`,
          impact: `Adds roughly ${Math.round(c.annualCost).toLocaleString('en-IN')} per year to your committed spending.`,
        });
      }
    }

    // ── annual-approaching ─────────────────────────────────────────────
    if ((c.frequency === 'annual' || c.frequency === 'yearly') && c.nextDate) {
      const daysToNext = (new Date(c.nextDate).getTime() - now) / 86400000;
      if (daysToNext >= 0 && daysToNext <= 30) {
        out.push({
          id: `annual-${c.id}`,
          type: 'annual-approaching',
          commitmentId: c.id,
          merchant: c.name,
          date: c.nextDate,
          headline: `Annual payment in ${Math.max(0, Math.round(daysToNext))} day${Math.round(daysToNext) === 1 ? '' : 's'} — ${c.name}`,
          detail: `A payment of roughly ${Math.round(c.amount).toLocaleString('en-IN')} is expected on ${fmtDateFull(c.nextDate)}.`,
          impact: `Ensure ${c.account} has sufficient balance.`,
        });
      }
    }

    // ── price-change ───────────────────────────────────────────────────
    // Requires ≥ 3 real matched transactions. We compute the mean of the
    // matched amounts and compare with the tracked recurring amount; the
    // most-recent tx must sit on the SAME side of `c.amount` as the mean,
    // so one outlier can't fabricate a trend.
    if (matches.length >= 3 && c.amount > 0) {
      const amounts = matches.map((t) => Math.abs(Number(t.amount) || 0));
      const avg = amounts.reduce((s, n) => s + n, 0) / amounts.length;
      const deltaPct = ((avg - c.amount) / c.amount) * 100;
      const lastAmt = amounts[amounts.length - 1];
      const meanSameSideAsLatest = (avg - c.amount) * (lastAmt - c.amount) > 0;
      if (Math.abs(deltaPct) >= 10 && meanSameSideAsLatest) {
        const goingUp = deltaPct > 0;
        // Confidence copy: reconciled links are exact; canonical is fuzzy.
        const evidence = source === 'reconciled' ? `${matches.length} linked charges` : `${matches.length} matching charges`;
        out.push({
          id: `price-${c.id}`,
          type: goingUp ? 'price-increase' : 'price-decrease',
          commitmentId: c.id,
          merchant: c.name,
          date: matches[matches.length - 1].txn_date || new Date().toISOString().slice(0, 10),
          headline: `${c.name} ${goingUp ? 'increased' : 'decreased'} by ~${Math.abs(Math.round(deltaPct))}%`,
          detail: `${evidence} averaged ${Math.round(avg).toLocaleString('en-IN')} vs the tracked amount of ${Math.round(c.amount).toLocaleString('en-IN')}.`,
          impact: goingUp
            ? `Adds ~${Math.round(Math.abs(avg - c.amount) * 12).toLocaleString('en-IN')} per year at the new price.`
            : `Saves ~${Math.round(Math.abs(avg - c.amount) * 12).toLocaleString('en-IN')} per year at the new price.`,
        });
      }
    }

    // ── commitment-stopped ────────────────────────────────────────────
    // Fires when: the commitment used to have matched txns (proving the
    // matcher works for it), AND the most recent match is older than
    // 2 × cadence-days, AND the cadence is sub-annual (so the 12-month
    // window is long enough to be conclusive).
    const cadence = CADENCE_DAYS[c.frequency] || 30;
    const isSubAnnual = c.frequency !== 'annual' && c.frequency !== 'yearly';
    if (isSubAnnual && matches.length >= 2) {
      const lastMatch = matches[matches.length - 1];
      const daysSince = (now - new Date(lastMatch.txn_date).getTime()) / 86400000;
      if (daysSince > cadence * 2 && daysSince <= 365) {
        // Only flag when we've missed at least 2 full cycles. Anything past
        // a year is likely already off the user's radar — avoid nagging.
        const cyclesMissed = Math.floor(daysSince / cadence);
        out.push({
          id: `stopped-${c.id}`,
          type: 'commitment-stopped',
          commitmentId: c.id,
          merchant: c.name,
          date: lastMatch.txn_date,
          headline: `${c.name} hasn't been charged in ${Math.round(daysSince)} days`,
          detail: `Last matching charge was ${fmtDateFull(lastMatch.txn_date)}. That's roughly ${cyclesMissed} ${FREQ_LABEL[c.frequency]?.toLowerCase() || c.frequency} cycles missed.`,
          impact: `If this commitment has ended, pausing or removing it will clean up your committed-money total (~${Math.round(c.monthlyEq).toLocaleString('en-IN')}/mo equivalent).`,
        });
      }
    }
  });

  // ── duplicate detection ──────────────────────────────────────────────
  // Anchoring nudge — a proper name-similarity match belongs on the backend.
  const subs = active.filter((c) => c.category === 'subscriptions');
  if (subs.length >= 2) {
    out.push({
      id: 'duplicate-subs',
      type: 'duplicate',
      commitmentId: null,
      merchant: 'Subscriptions',
      date: new Date().toISOString().slice(0, 10),
      headline: `${subs.length} active subscriptions — worth a check-in`,
      detail: `You're tracking ${subs.length} subscription-category commitments totalling ${Math.round(subs.reduce((s, c) => s + c.monthlyEq, 0)).toLocaleString('en-IN')}/month.`,
      impact: 'Overlapping services (e.g. two music apps, two cloud drives) often add up to real annual spend.',
    });
  }

  return out.sort((a, b) => b.date.localeCompare(a.date));
}

// Visual palette for each change type (dark-mode-safe via inline colors + a
// muted opacity for the background).
function changeStyle(type) {
  switch (type) {
    case 'price-increase':     return { color: '#B07210', bg: '#FBF5E8', icon: '↑' };
    case 'price-decrease':     return { color: '#3D7A63', bg: '#EEF2EF', icon: '↓' };
    case 'new-commitment':     return { color: '#3E6A80', bg: '#EEF5FA', icon: '+' };
    case 'annual-approaching': return { color: '#B07210', bg: '#FBF5E8', icon: '!' };
    case 'commitment-stopped': return { color: '#9B7A3F', bg: '#FBF5E8', icon: '⊘' };
    case 'duplicate':          return { color: '#3E6A80', bg: '#EEF5FA', icon: '≡' };
    default:                   return { color: '#9B9890', bg: '#F2F1EE', icon: '•' };
  }
}

// ─── Changes tab ────────────────────────────────────────────────────────────
function ChangesTab({ commitments, transactions, onOpenCommitment }) {
  const [filter, setFilter] = useState('all');
  const changes = useMemo(() => detectChanges({ commitments, transactions }), [commitments, transactions]);

  const filterMap = {
    all: null,
    price: ['price-increase', 'price-decrease'],
    new: ['new-commitment'],
    annual: ['annual-approaching'],
    stopped: ['commitment-stopped'],
    duplicate: ['duplicate'],
  };
  const shown = filter === 'all' ? changes : changes.filter((c) => filterMap[filter].includes(c.type));

  const filters = [
    { id: 'all',       label: 'All' },
    { id: 'price',     label: 'Price changes' },
    { id: 'new',       label: 'New' },
    { id: 'annual',    label: 'Annual approaching' },
    { id: 'stopped',   label: 'Stopped' },
    { id: 'duplicate', label: 'Overlaps' },
  ];

  const counts = {
    all: changes.length,
    price: changes.filter((c) => c.type === 'price-increase' || c.type === 'price-decrease').length,
    new: changes.filter((c) => c.type === 'new-commitment').length,
    annual: changes.filter((c) => c.type === 'annual-approaching').length,
    stopped: changes.filter((c) => c.type === 'commitment-stopped').length,
    duplicate: changes.filter((c) => c.type === 'duplicate').length,
  };

  const openTarget = (change) => {
    if (!change.commitmentId) return;
    const c = commitments.find((x) => x.id === change.commitmentId);
    if (c) onOpenCommitment(c);
  };

  return (
    <div className="space-y-5" data-testid="recurring-changes">
      {/* Filter pills */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {filters.map((f) => (
          <Pill
            key={f.id}
            active={filter === f.id}
            onClick={() => setFilter(f.id)}
            testId={`recurring-changes-filter-${f.id}`}
          >
            {f.label}
            {counts[f.id] > 0 && (
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${filter === f.id ? 'bg-white/20' : 'bg-border'}`}>
                {counts[f.id]}
              </span>
            )}
          </Pill>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card className="py-12 px-6 text-center">
          <p className="text-sm text-charcoal">Nothing has changed recently.</p>
          <p className="text-xs text-muted-foreground mt-1">
            When new commitments are detected, prices change, or annual payments approach, they&apos;ll appear here.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {shown.map((ch) => {
            const s = changeStyle(ch.type);
            const canOpen = Boolean(ch.commitmentId);
            return (
              <Card key={ch.id} className="overflow-hidden" testId={`recurring-change-${ch.id}`}>
                <div className="flex items-start gap-4 px-5 py-4">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center font-semibold text-sm flex-shrink-0"
                    style={{ backgroundColor: s.bg, color: s.color }}
                    aria-hidden="true"
                  >
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal">{ch.headline}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{ch.detail}</p>
                    <p className="text-xs text-charcoal/70 mt-1.5 leading-relaxed">{ch.impact}</p>
                    {canOpen && (
                      <button
                        type="button"
                        onClick={() => openTarget(ch)}
                        className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-70"
                        style={{ color: s.color }}
                        data-testid={`recurring-change-open-${ch.id}`}
                      >
                        View commitment <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
// Read-only view + Mark reviewed action + native Edit/Pause/Delete/Mark-paid
// buttons (Phase 82.12 CRUD port). Previously this drawer handed off to the
// legacy /recurring/manage page for every mutation.
function CommitmentDrawer({
  commitment, onClose, baseCurrency,
  onMarkReviewed, onEdit, onTogglePause, onDelete, onMarkPaid,
}) {
  const [reviewing, setReviewing] = useState(false);
  const [pausing,   setPausing]   = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  if (!commitment) return null;
  const c = commitment;
  const isIncomeLike = c.kind === 'income';
  const markPaidLabel = isIncomeLike ? 'Mark received this cycle' : 'Mark paid this cycle';

  // Human-friendly "N days ago" / "Never" for the reviewed row.
  const reviewedLabel = c.lastReviewed
    ? (() => {
        const days = Math.max(0, Math.round((Date.now() - new Date(c.lastReviewed).getTime()) / 86400000));
        if (days === 0)  return 'Today';
        if (days === 1)  return 'Yesterday';
        if (days < 30)   return `${days} days ago`;
        if (days < 365)  return `${Math.round(days / 30)} months ago`;
        return `${(days / 365).toFixed(1)} years ago`;
      })()
    : 'Never';

  const handleReview = async () => {
    setReviewing(true);
    await onMarkReviewed(c.id);
    setReviewing(false);
  };
  const handleTogglePause = async () => {
    setPausing(true);
    await onTogglePause(c);
    setPausing(false);
  };
  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    await onMarkPaid(c);
    setMarkingPaid(false);
  };
  const handleConfirmDelete = async () => {
    setDeleting(true);
    const ok = await onDelete(c);
    setDeleting(false);
    setConfirmDelete(false);
    if (ok) onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="recurring-detail-drawer" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-card w-full max-w-md h-full overflow-y-auto border-l border-border shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <p className="text-sm font-semibold text-charcoal truncate">{c.name}</p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-offwhite text-muted-foreground hover:text-charcoal flex items-center justify-center transition-colors"
            data-testid="recurring-detail-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Amount</p>
            <p className="font-display text-3xl font-medium text-charcoal mt-1" style={{ letterSpacing: '-0.02em' }}>
              {formatMoney(c.amount, c.currency || baseCurrency, { decimals: 0 })}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {FREQ_LABEL[c.frequency] || c.frequency} · {formatMoney(c.monthlyEq, c.currency || baseCurrency, { decimals: 0 })}/month equivalent
              {!c.active && <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">Paused</span>}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Category',      value: c.category.charAt(0).toUpperCase() + c.category.slice(1) },
              { label: 'Essentiality',  value: c.essentiality.charAt(0).toUpperCase() + c.essentiality.slice(1) },
              { label: 'Next payment',  value: c.nextDate ? fmtDateFull(c.nextDate) : '—' },
              { label: 'Annual cost',   value: formatMoney(c.annualCost, c.currency || baseCurrency, { decimals: 0 }) },
              { label: 'Account',       value: c.account },
              { label: 'Kind',          value: c.kind.replace('_', ' ') },
              { label: 'Last reviewed', value: reviewedLabel },
            ].map((row) => (
              <div key={row.label}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium">{row.label}</p>
                <p className="text-sm text-charcoal mt-1 capitalize">{row.value}</p>
              </div>
            ))}
          </div>

          {/* Mark this cycle paid/received. Writes an idempotent txn and
              advances next_due by one cadence — parity with legacy page. */}
          <div className="rounded-xl bg-offwhite border border-border/60 p-4 space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {isIncomeLike
                ? 'Confirm this cycle when you receive the money. We\u2019ll roll the next expected date forward.'
                : 'Confirm this cycle after you\u2019ve paid. We\u2019ll roll the next due date forward.'}
            </p>
            <button
              type="button"
              onClick={handleMarkPaid}
              disabled={markingPaid || !c.active}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-evergreen/30 text-evergreen text-xs font-medium hover:bg-evergreen/5 transition-colors disabled:opacity-50"
              data-testid="recurring-detail-mark-paid"
            >
              {markingPaid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {markPaidLabel}
            </button>
          </div>

          {/* Mark reviewed — writes last_reviewed=now() on recurring_expenses. */}
          <div className="rounded-xl bg-offwhite border border-border/60 p-4 space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Reviewing a commitment periodically catches price rises, duplicates, and services you no longer use.
            </p>
            <button
              type="button"
              onClick={handleReview}
              disabled={reviewing}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-evergreen/30 text-evergreen text-xs font-medium hover:bg-evergreen/5 transition-colors disabled:opacity-50"
              data-testid="recurring-detail-mark-reviewed"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {reviewing ? 'Saving\u2026' : 'Mark as reviewed'}
            </button>
          </div>

          {/* Manage: native Edit / Pause / Delete controls (Phase 82.12) */}
          <div className="rounded-xl bg-offwhite border border-border/60 p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Manage</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onEdit(c)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-charcoal text-xs font-medium hover:bg-offwhite transition-colors"
                data-testid="recurring-detail-edit"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                type="button"
                onClick={handleTogglePause}
                disabled={pausing}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-charcoal text-xs font-medium hover:bg-offwhite transition-colors disabled:opacity-50"
                data-testid="recurring-detail-toggle-active"
              >
                {pausing
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : (c.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />)}
                {c.active ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-destructive/30 text-destructive text-xs font-medium hover:bg-destructive/5 transition-colors ml-auto"
                data-testid="recurring-detail-delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="recurring-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this commitment?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the recurring rule for {c.name}. Any transactions you’ve already recorded stay put — only future automations are removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recurring-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="recurring-delete-confirm-action"
            >
              {deleting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'upcoming',    label: 'Upcoming' },
  { id: 'commitments', label: 'Commitments' },
  { id: 'changes',     label: 'Changes' },
];

export default function RecurringPage() {
  usePageTitle('Recurring');
  const { profile } = useAuth();
  const navigate = useNavigate();
  const baseCurrency = (profile?.base_currency || profile?.currency || 'INR').toUpperCase();
  const {
    commitments: rawCommitments, transactions, loading,
    refresh, markReviewed, togglePause, deleteCommitment, markPaid,
  } = useRecurringData();

  // Phase 84.22.5 — recurring commitments live in whatever currency their
  // linked account uses (INR SIPs, AED rent, USD subscriptions). Every
  // aggregate on this page must therefore FX-convert into the user's
  // base currency before summing. We derive `monthlyEqBase` /
  // `annualCostBase` / `amountBase` up front and downstream calculations
  // use those.
  // Phase 84.22.9 (Wave B2) — use the app-root CurrencyContext instead
  // of the per-page useFxRates hook. Same FX table as every other page,
  // no duplicate fetches, no drift.
  const currencyCtx = useCurrencyContext();
  const commitments = useMemo(() => rawCommitments.map((c) => {
    const amountBase = currencyCtx.toBase(c.amount, c.currency);
    return {
      ...c,
      amountBase,
      monthlyEqBase: currencyCtx.toBase(c.monthlyEq, c.currency),
      annualCostBase: currencyCtx.toBase(c.annualCost, c.currency),
    };
  }), [rawCommitments, currencyCtx]);
  const [tab, setTab] = useState('overview');
  const [drawer, setDrawer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Bumping this refreshes the pill count when a suggestion is accepted /
  // dismissed / scanned inside the drawer.
  const [suggestionsRefreshKey, setSuggestionsRefreshKey] = useState(0);
  const { count: suggestionCount } = useSuggestionCount(suggestionsRefreshKey);

  const openCreate = () => {
    setDialogInitial(null);
    setDialogOpen(true);
  };

  const openEditFromDrawer = (c) => {
    // Pass raw shape the dialog understands (kind, name, amount, category,
    // frequency, next_due, linked_*). We reuse `nextDate` fallback inside
    // the dialog seed to bridge the naming difference.
    setDialogInitial({
      id: c.id,
      kind: c.kind,
      name: c.name,
      amount: c.amount,
      category: c.category,
      frequency: c.frequency,
      nextDate: c.nextDate,
      active: c.active,
    });
    setDialogOpen(true);
  };

  // "Mark reviewed" from the drawer needs to also update the drawer's local
  // view so the "Last reviewed" label refreshes without a re-open.
  const handleMarkReviewed = async (id) => {
    const ok = await markReviewed(id);
    if (ok && drawer && drawer.id === id) {
      setDrawer({ ...drawer, lastReviewed: new Date().toISOString() });
    }
    return ok;
  };

  const handleTogglePause = async (c) => {
    const ok = await togglePause(c);
    if (ok && drawer && drawer.id === c.id) {
      setDrawer({ ...drawer, active: !c.active });
    }
    return ok;
  };

  const handleMarkPaid = async (c) => {
    const newDueISO = await markPaid(c);
    if (newDueISO && drawer && drawer.id === c.id) {
      setDrawer({ ...drawer, nextDate: newDueISO });
    }
    return !!newDueISO;
  };

  const handleDelete = async (c) => deleteCommitment(c);

  const handleSaved = () => {
    refresh();
    // If we were editing an existing commitment, close the drawer so the
    // parent list re-renders with the fresh row.
    setDrawer(null);
  };

  const handleSuggestionAccepted = () => {
    refresh();
    setSuggestionsRefreshKey((k) => k + 1);
  };

  return (
    <div className="space-y-6" data-testid="recurring-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-medium text-charcoal" style={{ letterSpacing: '-0.015em' }}>
            Recurring
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your committed money — the payments that repeat before everyday spending begins.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {suggestionCount > 0 && (
            <button
              type="button"
              onClick={() => setSuggestionsOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid="recurring-detected-pill"
              aria-label={`${suggestionCount} detected recurring patterns`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Detected ({suggestionCount})
            </button>
          )}
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-lg bg-evergreen text-white hover:bg-evergreen-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            data-testid="recurring-add"
          >
            <Plus className="h-3.5 w-3.5" /> Add commitment
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-border/60 overflow-x-auto" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative whitespace-nowrap ${
                active ? 'text-evergreen' : 'text-muted-foreground hover:text-foreground'
              }`}
              role="tab"
              aria-selected={active}
              data-testid={`recurring-tab-${t.id}`}
            >
              {t.label}
              {active && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-evergreen rounded-t-sm" />}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground text-sm justify-center" data-testid="recurring-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your commitments…
        </div>
      ) : commitments.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="Track your first recurring commitment"
          body="Rent, EMIs, subscriptions, SIPs — anything that hits your account on a schedule. Once you tell 2120 about them, we'll surface your true committed money each month."
          primaryCta={{
            label: 'Add your first commitment',
            onClick: openCreate,
            testid: 'recurring-empty-add',
          }}
          secondaryCta={{
            label: 'Explore Demo Account',
            onClick: () => navigate('/settings?tab=demo'),
            testid: 'recurring-empty-secondary',
          }}
          testid="recurring-empty-state"
        />
      ) : (
        <>
          {/* Each tab is isolated so a failure in change detection (e.g.
              malformed txn history) doesn't blank out the whole hub. */}
          {tab === 'overview' && (
            <SectionErrorBoundary label="Recurring Overview">
              <OverviewTab
                commitments={commitments}
                transactions={transactions}
                baseCurrency={baseCurrency}
                onGoToTab={setTab}
                onOpenCommitment={setDrawer}
              />
            </SectionErrorBoundary>
          )}
          {tab === 'upcoming' && (
            <SectionErrorBoundary label="Recurring Upcoming">
              <UpcomingTab commitments={commitments} baseCurrency={baseCurrency} />
            </SectionErrorBoundary>
          )}
          {tab === 'commitments' && (
            <SectionErrorBoundary label="Recurring Commitments">
              <CommitmentsTab
                commitments={commitments}
                baseCurrency={baseCurrency}
                onOpenCommitment={setDrawer}
              />
            </SectionErrorBoundary>
          )}
          {tab === 'changes' && (
            <SectionErrorBoundary label="Recurring Changes">
              <ChangesTab
                commitments={commitments}
                transactions={transactions}
                onOpenCommitment={setDrawer}
              />
            </SectionErrorBoundary>
          )}
        </>
      )}

      <CommitmentDrawer
        commitment={drawer}
        onClose={() => setDrawer(null)}
        baseCurrency={baseCurrency}
        onMarkReviewed={handleMarkReviewed}
        onEdit={openEditFromDrawer}
        onTogglePause={handleTogglePause}
        onDelete={handleDelete}
        onMarkPaid={handleMarkPaid}
      />

      <CommitmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={dialogInitial}
        onSaved={handleSaved}
      />

      <SuggestionsDrawer
        open={suggestionsOpen}
        onOpenChange={setSuggestionsOpen}
        currency={baseCurrency}
        onAccepted={handleSuggestionAccepted}
      />
    </div>
  );
}
